import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  noindex?: boolean;
  structuredData?: object | object[];
  author?: string;
  publishedDate?: string;
  modifiedDate?: string;
  articleSection?: string;
  breadcrumbs?: Array<{ name: string; url: string }>;
}

export const SEO: React.FC<SEOProps> = ({
  title = 'Vitly - Find Fair Meeting Spots for Your Group in Seconds',
  description = 'Stop debating where to meet. Vitly finds venues that are equally convenient for everyone, saving your group hours of back-and-forth. Free for up to 5 participants.',
  keywords = 'fair meeting spot finder, meetup coordinator, group venue finder, midpoint meeting place, where to meet friends, equidistant location finder, group planning app',
  canonical,
  ogImage = 'https://www.getvitly.com/fbpreview1.png',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  noindex = false,
  structuredData,
  author = 'Vitly Team',
  publishedDate,
  modifiedDate,
  articleSection,
  breadcrumbs
}) => {
  const siteUrl = 'https://www.getvitly.com';
  const fullTitle = title.includes('Vitly') ? title : `${title} | Vitly`;
  const canonicalUrl = canonical ? `${siteUrl}${canonical}` : (typeof window !== 'undefined' ? window.location.href : siteUrl);

  // Organization Schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${siteUrl}/#organization`,
    name: 'Vitly',
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: `${siteUrl}/logo.png`,
      width: 512,
      height: 512
    },
    description: 'Smart meetup coordination platform that helps groups find perfect meeting spots',
    foundingDate: '2024-01-01',
    founders: [{
      '@type': 'Person',
      name: 'Vitly Team',
      jobTitle: 'Founder'
    }],
    address: {
      '@type': 'PostalAddress',
      streetAddress: '123 Innovation Drive',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94105',
      addressCountry: 'US'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+1-888-VITLY-APP',
      contactType: 'Customer Support',
      email: 'support@getvitly.com',
      availableLanguage: ['English', 'Spanish', 'French', 'German', 'Portuguese'],
      areaServed: 'Worldwide'
    },
    sameAs: [
      'https://twitter.com/getvitly',
      'https://www.linkedin.com/company/getvitly'
    ]
  };

  // WebSite Schema with SearchAction
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    url: siteUrl,
    name: 'Vitly',
    description: 'The smart way to coordinate group meetups',
    publisher: {
      '@id': `${siteUrl}/#organization`
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/search?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    },
    inLanguage: 'en-US'
  };

  // WebApplication Schema
  const webAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    '@id': `${siteUrl}/#webapp`,
    name: 'Vitly',
    description: 'Smart platform for finding optimal meeting locations',
    url: siteUrl,
    applicationCategory: 'LifestyleApplication',
    applicationSubCategory: 'Social Coordination',
    operatingSystem: 'Any',
    browserRequirements: 'Requires JavaScript. Works with Chrome 90+, Firefox 88+, Safari 14+, Edge 90+',
    permissions: 'location',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      priceValidUntil: '2026-12-31',
      availability: 'https://schema.org/InStock',
      seller: {
        '@id': `${siteUrl}/#organization`
      }
    },
    screenshot: [
      `${siteUrl}/screenshots/dashboard.png`,
      `${siteUrl}/screenshots/map-view.png`,
      `${siteUrl}/screenshots/suggestions.png`
    ],
    featureList: [
      'Smart location optimization',
      'Real-time participant tracking',
      'Smart venue recommendations',
      'Group management system',
      'Analytics dashboard',
      'WebSocket notifications',
      'Google Maps integration',
      'Multi-language support'
    ],
    softwareVersion: '2.0.0',
    datePublished: '2024-01-01',
    dateModified: '2024-08-21',
    creator: {
      '@id': `${siteUrl}/#organization`
    }
  };

  // VideoObject Schema for hero video
  const videoSchema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: 'Vitly Demo: Find Fair Meeting Spots in Seconds',
    description: 'See how Vitly calculates the optimal meeting point for your group by analyzing everyone\'s location and suggesting nearby venues that minimize total travel time.',
    thumbnailUrl: `${siteUrl}/fbpreview1.png`,
    uploadDate: '2025-01-01T00:00:00Z',
    duration: 'PT30S',
    contentUrl: `${siteUrl}/vitly-hero-1080p.mp4`,
    embedUrl: `${siteUrl}/embed/demo`,
    potentialAction: {
      '@type': 'WatchAction',
      target: siteUrl
    },
    publisher: {
      '@id': `${siteUrl}/#organization`
    }
  };

  // BreadcrumbList Schema
  const breadcrumbSchema = breadcrumbs ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${siteUrl}${crumb.url}`
    }))
  } : null;

  // FAQ Schema
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How does Vitly find the perfect meeting spot?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Vitly calculates the geographic midpoint of all participants and analyzes nearby venues using Google Maps data. The fairness algorithm evaluates driving distances and travel times to find spots that minimize total travel time for everyone, ensuring no one always gets stuck with the longest commute.'
        }
      },
      {
        '@type': 'Question',
        name: 'Is Vitly free to use?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes! Vitly offers a free Hangout Essentials tier with 3 meetups per month and up to 5 friends per meetup. Social Squad at $4.99/month (or $49.99/year) unlocks 10 meetups per month and up to 20 friends, plus a 14-day trial and priority support. Calendar support is ICS download/import — not recurring meetups or Google Calendar OAuth.'
        }
      },
      {
        '@type': 'Question',
        name: 'How many people can participate in a Vitly meetup?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Free accounts (Hangout Essentials) support up to 5 friends per meetup. Social Squad accounts support up to 20 friends per meetup. The fairness algorithm works efficiently for any group size within your plan limits.'
        }
      },
      {
        '@type': 'Question',
        name: 'Does Vitly work internationally?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes! Vitly works anywhere Google Maps has coverage. You can coordinate meetups across cities and regions worldwide. The interface is currently in English.'
        }
      },
      {
        '@type': 'Question',
        name: 'How does Vitly protect user privacy and location data?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Vitly uses TLS encryption for data in transit and Supabase Row-Level Security to protect your data. Location data is only shared with meetup participants you explicitly invite. We never sell user data.'
        }
      },
      {
        '@type': 'Question',
        name: 'Can I add meetups to my calendar?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes! Vitly generates ICS calendar files that you can download and import into Google Calendar, Outlook, Apple Calendar, or any calendar app that supports ICS format.'
        }
      },
      {
        '@type': 'Question',
        name: 'What makes Vitly different from other meeting coordinators?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Vitly focuses on fairness - finding spots where everyone travels a similar distance, not just the geographic center. We integrate with Google Maps for real-time travel estimates and Google Places for venue recommendations. Plus, guests can participate without creating an account.'
        }
      },
      {
        '@type': 'Question',
        name: 'How accurate are Vitly\'s venue recommendations?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Vitly uses Google Places data for venue recommendations, giving you access to ratings, reviews, hours, and pricing information. You can filter by venue category (restaurants, cafes, bars, etc.) to find spots that match what your group is looking for.'
        }
      },
      {
        '@type': 'Question',
        name: 'Do guests need to create an account to participate?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'No, guests can participate without creating an account. Just share the meetup link and they can add their location instantly. This removes friction and makes it easy for everyone to join, whether they\'re tech-savvy or not. Only the meetup organizer needs a free account.'
        }
      },
      {
        '@type': 'Question',
        name: 'How does Vitly calculate the best meeting spot?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Vitly calculates the geographic centroid of all participants\' starting locations, then analyzes nearby venues based on driving time, venue ratings, and category preferences. The algorithm uses Google Maps data to suggest spots that minimize total travel time for everyone in your group.'
        }
      }
    ]
  };

  // Article Schema (if it's a blog post or article)
  const articleSchema = articleSection ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: description,
    author: {
      '@type': 'Person',
      name: author
    },
    datePublished: publishedDate,
    dateModified: modifiedDate || publishedDate,
    publisher: {
      '@id': `${siteUrl}/#organization`
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl
    },
    articleSection: articleSection,
    image: ogImage
  } : null;

  // Combine all schemas
  const allSchemas: object[] = [
    organizationSchema,
    websiteSchema,
    webAppSchema,
    faqSchema,
    videoSchema
  ];

  if (breadcrumbSchema) allSchemas.push(breadcrumbSchema as object);
  if (articleSchema) allSchemas.push(articleSchema as object);

  if (structuredData) {
    if (Array.isArray(structuredData)) {
      allSchemas.push(...(structuredData as object[]));
    } else {
      allSchemas.push(structuredData as object);
    }
  }

  return (
    <Helmet>
      {/* HTML and Language */}
      <html lang="en" />
      
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Mobile & Performance */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      <meta name="theme-color" content="#10b981" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Vitly - Smart Meeting Coordination Platform" />
      <meta property="og:site_name" content="Vitly" />
      <meta property="og:locale" content="en_US" />
      
      {/* Article metadata if applicable */}
      {publishedDate && <meta property="article:published_time" content={publishedDate} />}
      {modifiedDate && <meta property="article:modified_time" content={modifiedDate} />}
      {articleSection && <meta property="article:section" content={articleSection} />}
      {author && <meta property="article:author" content={author} />}
      
      {/* Twitter */}
      <meta property="twitter:card" content={twitterCard} />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={ogImage} />
      <meta property="twitter:site" content="@vitlyapp" />
      <meta property="twitter:creator" content="@vitlyapp" />
      
      {/* Robots & Indexing */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      
      {/* Geo Targeting */}
      <meta name="geo.region" content="US" />
      <meta name="geo.placename" content="San Francisco" />
      <meta name="geo.position" content="37.7749;-122.4194" />
      <meta name="ICBM" content="37.7749, -122.4194" />
      
      {/* Language Alternates */}
      <link rel="alternate" hrefLang="en" href={canonicalUrl} />
      <link rel="alternate" hrefLang="es" href={`${siteUrl}/es${canonical || '/'}`} />
      <link rel="alternate" hrefLang="fr" href={`${siteUrl}/fr${canonical || '/'}`} />
      <link rel="alternate" hrefLang="de" href={`${siteUrl}/de${canonical || '/'}`} />
      <link rel="alternate" hrefLang="pt" href={`${siteUrl}/pt${canonical || '/'}`} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
      
      {/* Preconnect to improve performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://maps.googleapis.com" />
      <link rel="preconnect" href="https://www.googletagmanager.com" />
      <link rel="dns-prefetch" href="https://api.getvitly.com" />
      
      {/* Additional SEO */}
      <meta httpEquiv="content-language" content="en" />
      <meta name="language" content="English" />
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      <meta name="revisit-after" content="7 days" />
      
      {/* Structured Data / JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': allSchemas
        })}
      </script>
    </Helmet>
  );
};

export type { SEOProps };
export default SEO;
